import { Col, DatePicker, Form, Row } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import type { BaseOptionType } from "antd/lib/select"
import React, { useCallback, useMemo, useState } from "react"

import { useDateFormat } from "../../../../../../hooks/use-date-format.ts"
import { dateAutoFill } from "../../../../../../utils/form/date-auto-fill.tsx"
import { type ProjectTypeDetail } from "../../../../types/project.types.ts"
import type { ProjectFormData } from "../../project-form-data.ts"
import { ProjectDetailsSectionProjectType } from "./ProjectDetailsSectionProjectTypeSelect.tsx"
import { ProjectTypeCategoryFields } from "./ProjectTypeCategoryFields.tsx"
import { TextInput } from "../../../../../../components/Input/TextInput.tsx"

interface ProjectDetailsSectionProps {
  form: FormInstance<ProjectFormData>
  isEdit?: boolean
  options: Record<string, BaseOptionType[]>
  projectTypeDetails: ProjectTypeDetail[]
}

export function ProjectDetailsSection({
  form,
  projectTypeDetails
}: ProjectDetailsSectionProps) {
  const { getDateValueProps, createDateRangeRules } = useDateFormat()
  const [selectedTypeNames, setSelectedTypeNames] = useState<
    Record<string, string>
  >({})

  const initialProjectTypes: BaseOptionType[] = useMemo(() => {
    return projectTypeDetails?.map((el) => ({
      value: el?.id,
      label: el?.name
    }))
  }, [projectTypeDetails])

  const assignedTypeIds = useMemo(
    () => projectTypeDetails?.map((el) => el?.id) || [],
    [projectTypeDetails]
  )

  const onTypeNamesChange = useCallback((names: Record<string, string>) => {
    setSelectedTypeNames((prev) => ({ ...prev, ...names }))
  }, [])

  return (
    <>
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item<ProjectFormData>
            label="Project Name"
            name="lead_name"
            rules={[
              {
                required: true,
                message: "Please enter name"
              }
            ]}
          >
            <TextInput placeholder="Enter project name" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Project Type"
            name="project_type"
            // to exclude onChange
            rules={[
              {
                required: true,
                message: "Please select project type"
              }
            ]}
          >
            <ProjectDetailsSectionProjectType
              form={form}
              initialOptions={initialProjectTypes}
              onTypeNamesChange={onTypeNamesChange}
            />
          </Form.Item>
        </Col>
      </Row>
      <ProjectTypeCategoryFields
        assignedTypeIds={assignedTypeIds}
        typeNames={selectedTypeNames}
      />
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item<ProjectFormData>
            label="Start date"
            name="start_date"
            dependencies={["end_date"]}
            getValueProps={getDateValueProps}
            rules={createDateRangeRules<ProjectFormData>("end_date", "start")}
          >
            <DatePicker
              className="w-full"
              onChange={dateAutoFill<ProjectFormData>({
                form,
                endDatePath: "end_date"
              })}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item<ProjectFormData>
            label="Due Date"
            name="end_date"
            dependencies={["start_date"]}
            getValueProps={getDateValueProps}
            rules={createDateRangeRules<ProjectFormData>("start_date", "end")}
          >
            {/* Start date fills this in; selecting it on focus lets a typed
                date replace that value instead of being appended to it. */}
            <DatePicker
              className="w-full"
              onFocus={(event) => (event.target as HTMLInputElement).select?.()}
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  )
}
